import { makeRailingLineworkForLevel } from "building-generation/utils/floorplan";
import type { Building } from "building-types/building/buildingTypes";
import type { RailingHandle } from "building-types/handles/docEntityHandleTypes";
import { RoomType } from "building-types/rooms/roomTypesEnum";
import type { GeneratedRailing } from "building-types/settings/railingTypes";
import type { ViewportRenderStyle } from "building-types/views/planSets";
import type { SheetGeometryCategoryTable } from "building-types/views/sheetGeometryCategory";
import { SheetGeometryCategory } from "building-types/views/sheetGeometryCategory";
import { sheetPolylineGeometryNew } from "building-types/views/sheetGeometryTypes";
import { PlanGeometryBuilder } from "doc/plans/planGeometrySource";
import type { PlanGeometryGeneratorResult } from "doc/plans/planGeometrySourceTypes";
import { railingMaskInlineStyle } from "doc/plans/planStyleSheets";
import { roomsOnLevel } from "doc/rooms/roomsOnLevel";
import { aabb2Overlaps } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import { offsetPolyLine2 } from "geom/offset";
import { polygonIntersection, polygonUnion } from "geom/polygon";
import { polyLine2AABB2, polyLine2Copy, polyLine2SetCCW } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";

interface PlanRailingProps {
  building: Building;
  levelId: string;
  renderOnlyStairRailings?: boolean;
  railingFilter?: (railing: GeneratedRailing) => boolean;
  railingPostFilter?: (railing: GeneratedRailing) => boolean | undefined;
  renderStyleMode?: ViewportRenderStyle;
}

const tagReplaceMap: SheetGeometryCategoryTable<SheetGeometryCategory> = {
  [SheetGeometryCategory.RAILING_PROJECTED]: SheetGeometryCategory.RAILING_PROJECTED_STAIR_ONLY,
  [SheetGeometryCategory.RAILING_ABOVE]: SheetGeometryCategory.RAILING_ABOVE_STAIR_ONLY,
  [SheetGeometryCategory.RAILING_POST]: SheetGeometryCategory.RAILING_POST_STAIR_ONLY,
};

export function makeSheetGeometryFromRailingsStairsOnly(props: PlanRailingProps) {
  const builder = new PlanGeometryBuilder();
  const railingPlanGeometry = makeSheetGeometryFromRailings({ ...props, renderOnlyStairRailings: true });
  builder.addAllFrom(railingPlanGeometry, tagReplaceMap);
  return builder;
}

export function makeSheetGeometryFromRailings({
  building,
  levelId,
  renderOnlyStairRailings = false,
  railingFilter,
  railingPostFilter,
  renderStyleMode,
}: PlanRailingProps): PlanGeometryGeneratorResult {
  const builder = new PlanGeometryBuilder();

  const { levels, rooms, roomsByLevel } = building;
  const levelRooms = roomsOnLevel(levelId, rooms, roomsByLevel);
  const wallConfig = building.settings.wall;
  const filteredRailings = Object.entries(building.railings)
    // TODO: extract these filters
    .filter(
      (entry) =>
        (!renderOnlyStairRailings || entry[1].associatedWithStairs) &&
        (railingFilter === undefined || railingFilter(entry[1])),
    )
    .reduce<Record<string, GeneratedRailing>>((previous, current) => {
      previous[current[0]] = current[1];
      return previous;
    }, {});

  if (renderStyleMode === "showroomFloorPlan") {
    return makeSheetGeometryFromShowroomRailings({
      levelId,
      railings: filteredRailings,
      railingPostFilter,
    });
  }

  makeRailingLineworkForLevel(filteredRailings, levelId).forEach((rl) => {
    const { solid, dashed, solidPost, railing, solidOBBs, dashedOBBs, solidPostOBBs } = rl;
    if (solid.length > 0) {
      builder.addGeometry(
        sheetPolylineGeometryNew({
          handle: levels[levelId].handle,
          paths: solid,
          buildingEntity: railing,
          relaxationObstacleOBBs: solidOBBs,
          ...railingMaskInlineStyle,
        }),
        SheetGeometryCategory.RAILING_PROJECTED,
      );
    }
    if (dashed.length > 0) {
      builder.addGeometry(
        sheetPolylineGeometryNew({
          handle: levels[levelId].handle,
          paths: dashed,
          buildingEntity: railing,
          relaxationObstacleOBBs: dashedOBBs,
        }),
        SheetGeometryCategory.RAILING_ABOVE,
      );
    }
    if (solidPost.length > 0 && (!railingPostFilter || railingPostFilter(railing))) {
      builder.addGeometry(
        sheetPolylineGeometryNew({
          handle: levels[levelId].handle,
          paths: solidPost,
          buildingEntity: railing,
          relaxationObstacleOBBs: solidPostOBBs,
          ...railingMaskInlineStyle,
        }),
        SheetGeometryCategory.RAILING_POST,
      );
    }
  });

  const level = levels[levelId];
  const levelBelowIndex = level.levelBelow ?? "";
  const levelBelow = levels[levelBelowIndex];

  if (!levelBelow) {
    return builder;
  }

  const openToBelowOutlines: PolyLine2[] = [];

  for (const room of levelRooms) {
    if (room.type === RoomType.OPEN_BELOW || room.type === RoomType.STAIRS || room.type === RoomType.STAIRS_EDITOR) {
      openToBelowOutlines.push(polyLine2Copy(room.outline));
    }
  }

  if (openToBelowOutlines.length < 1) {
    return builder;
  }

  const openBelowOutlinesJoined: PolyLine2[] = polygonUnion(openToBelowOutlines.map((x) => [x])).map((x) => x[0]);
  for (const openBelowOutline of openBelowOutlinesJoined) {
    const openBelowOutlineExpanded = offsetPolyLine2(polyLine2SetCCW(openBelowOutline), -wallConfig.interiorWallOffset);
    const openBelowOutlineBB = polyLine2AABB2(openBelowOutlineExpanded);

    const clipRailingLineworkForOpening = (linework: PolyLine2[]) => {
      return clipRailingLineworkBelow(linework, openBelowOutlineExpanded, openBelowOutlineBB);
    };

    makeRailingLineworkForLevel(filteredRailings, levelBelowIndex).forEach((rl) => {
      const { solid, dashed, solidPost, railing, solidOBBs, dashedOBBs, solidPostOBBs } = rl;

      const levelBelowSolid = clipRailingLineworkForOpening(solid);
      const levelBelowDashed = clipRailingLineworkForOpening(dashed);
      const levelBelowSolidPost = clipRailingLineworkForOpening(solidPost);

      if (levelBelowSolid.length > 0) {
        builder.addGeometry(
          sheetPolylineGeometryNew({
            handle: levelBelow.handle,
            paths: levelBelowSolid,
            buildingEntity: railing,
            relaxationObstacleOBBs: solidOBBs,
            ...railingMaskInlineStyle,
          }),
          SheetGeometryCategory.RAILING_PROJECTED,
        );
      }
      if (levelBelowDashed.length > 0) {
        builder.addGeometry(
          sheetPolylineGeometryNew({
            handle: levelBelow.handle,
            paths: levelBelowDashed,
            buildingEntity: railing,
            relaxationObstacleOBBs: dashedOBBs,
            ...railingMaskInlineStyle,
          }),
          SheetGeometryCategory.RAILING_PROJECTED,
        );
      }
      if (levelBelowSolidPost.length > 0 && (!railingPostFilter || railingPostFilter(railing))) {
        builder.addGeometry(
          sheetPolylineGeometryNew({
            handle: levelBelow.handle,
            paths: levelBelowSolidPost,
            buildingEntity: railing,
            relaxationObstacleOBBs: solidPostOBBs,
            ...railingMaskInlineStyle,
          }),
          SheetGeometryCategory.RAILING_POST,
        );
      }
    });
  }

  return builder;
}

function makeSheetGeometryFromShowroomRailings({
  levelId,
  railings,
  railingPostFilter,
}: {
  levelId: string;
  railings: Record<string, GeneratedRailing>;
  railingPostFilter?: (railing: GeneratedRailing) => boolean | undefined;
}): PlanGeometryGeneratorResult {
  const builder = new PlanGeometryBuilder();

  for (const railingId in railings) {
    const railing = railings[railingId];
    if (railing.levelId !== levelId) {
      continue;
    }

    const handle: RailingHandle = { type: "RAILING", id: railingId };

    // No railingMaskInlineStyle here: showroom railing categories override fill to false in the view template.
    if (railing.pathThickened.length > 0) {
      builder.addGeometry(
        sheetPolylineGeometryNew({
          handle,
          paths: [railing.pathThickened],
          buildingEntity: railing,
        }),
        SheetGeometryCategory.RAILING_PROJECTED,
      );
    }

    if (railing.path.length > 0) {
      builder.addGeometry(
        sheetPolylineGeometryNew({
          handle,
          paths: [railing.path],
          buildingEntity: railing,
        }),
        SheetGeometryCategory.RAILING_ABOVE,
      );
    }

    if (railingPostFilter && !railingPostFilter(railing)) {
      continue;
    }

    const posts = railing.railingLinework2[levelId]?.postPolyLine2s ?? [];
    if (posts.length === 0) {
      continue;
    }

    builder.addGeometry(
      sheetPolylineGeometryNew({
        handle,
        paths: posts.map((post) => post.polyLine2),
        buildingEntity: railing,
      }),
      SheetGeometryCategory.RAILING_POST,
    );
  }

  return builder;
}

function clipRailingLineworkBelow(
  railingLinework: PolyLine2[],
  openBelowOutline: PolyLine2,
  openBelowOutlineAABB: AABB2,
): PolyLine2[] {
  const clippedLinework: PolyLine2[] = [];

  // TODO: if polygonIntersection checks for aabb overlap, we should just skip iterating
  // over each polyline and do the intersection on all of them at once
  for (const pl of railingLinework) {
    const plAABB = polyLine2AABB2(pl);

    if (!aabb2Overlaps(openBelowOutlineAABB, plAABB)) {
      continue;
    }

    clippedLinework.push(...polygonIntersection([[pl], [openBelowOutline]]).map((x) => x[0]));
  }

  return clippedLinework;
}
